import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { getEffectiveChecklistStatus, isRowApproved } from '../../manualOverridePersistence';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import BooleanFlag from '../../reportCards/BooleanFlag';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import RuleStatusCard from '../../reportCards/RuleStatusCard';
import { formatContextDate } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import { FESTIVE_DATE_LABEL } from './blackoutRules';
import type { BlackoutChecklistRow, BlackoutCoverageSummary } from './types';

interface Props {
  coverage: BlackoutCoverageSummary;
  coverageRow?: BlackoutChecklistRow;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<BlackoutChecklistRow>;
  showAutoApproved: boolean;
  travelFrom?: string;
  travelTo?: string;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

const APPROVED_ROW_SX = {
  '& > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.08),
  },
  '&:hover > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.14),
  },
  '& .MuiInputBase-root': {
    bgcolor: 'background.paper',
  },
} as const;

export default function BlackoutReportCard({
  coverage,
  coverageRow,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  travelFrom,
  travelTo,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const coverageApproved = coverageRow ? isRowApproved(coverageRow, manualOverrides) : false;
  return (
    <ReportCard>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Blackout Name</TableCell>
              <TableCell>Date Range</TableCell>
              <TableCell>Within Travel Validity</TableCell>
              <TableCell>Clash with Gala / Festive Dates</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={5} label="No blackout rows match this view." />
            ) : (
              rows.map((row) => {
                const approved = isRowApproved(row, manualOverrides);

                return (
                  <TableRow key={row.id} hover sx={approved ? APPROVED_ROW_SX : undefined}>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatContextDate(row.from)} - {formatContextDate(row.to)}
                    </TableCell>
                    <TableCell>
                      <BooleanFlag value={row.withinTravelValidity} />
                    </TableCell>
                    <TableCell>
                      <BooleanFlag falseIsGood value={!row.festiveClear} />
                    </TableCell>
                    <TableCell>
                      <ApprovalActionButton
                        approved={approved}
                        onApprove={() => onApprovalChange(row.id)}
                        onUnapprove={() => onUnapprovalChange(row.id)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ p: 2 }}>
        <RuleStatusCard
          icon={<InfoOutlinedIcon fontSize="small" />}
          status={coverageRow ? getEffectiveChecklistStatus(coverageRow, manualOverrides) : coverage.status}
          title={
            <>
              Blackout coverage rule:
              <Typography component="span" variant="caption" fontWeight={400} color="text.secondary">
                {' '}
                cumulative blackout dates must cover less than <strong>75%</strong> of the travel period.
              </Typography>
            </>
          }
          action={
            coverageRow && (
              <ApprovalActionButton
                approved={coverageApproved}
                onApprove={() => onApprovalChange(coverageRow.id)}
                onUnapprove={() => onUnapprovalChange(coverageRow.id)}
              />
            )
          }
        >
          <Box component="ul" sx={{ mb: 0, mt: 0, pl: 2.25 }}>
            <Typography component="li" variant="caption" color="text.secondary">
              <strong>Travel dates:</strong> {formatContextDate(travelFrom)} - {formatContextDate(travelTo)}
              {coverage.travelDays ? ` (${coverage.travelDays} total days)` : ''}
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              <strong>Total blackout days:</strong> {coverage.blackoutDays} ({coverage.percent}% of travel period)
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              <strong>Festive dates checked:</strong> {FESTIVE_DATE_LABEL}. Rows that overlap any of these dates require
              approval.
            </Typography>
          </Box>
        </RuleStatusCard>
      </Box>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="blackout row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
